import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { CostBreakdown } from "../types";

function isoDate(offsetDays: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function CostDashboardPage() {
  const { project } = useProject();
  const { call } = useApi();
  const [from, setFrom] = useState(isoDate(-30));
  const [to, setTo] = useState(isoDate(0));
  const [data, setData] = useState<CostBreakdown | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    const params = new URLSearchParams({
      from: `${from}T00:00:00Z`,
      to: `${to}T23:59:59Z`,
    });
    call<CostBreakdown>(`/api/projects/${project.projectId}/cost?${params.toString()}`)
      .then((d) => setData(d))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [call, project.projectId, from, to]);

  return (
    <>
      <div className="page-header">
        <h1>Cost Dashboard</h1>
        <p>Token usage and Bedrock cost across the selected window.</p>
      </div>

      <div className="card">
        <div className="grid-3">
          <div className="field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p>Loading…</p>}
      {data && (
        <>
          <CostChartCard title="By epic" buckets={data.byEpic} />
          <CostChartCard title="By agent" buckets={data.byAgent} />
          <CostChartCard title="By model" buckets={data.byModel} />
        </>
      )}
    </>
  );
}

interface CardProps {
  title: string;
  buckets: CostBreakdown["byEpic"];
}

function CostChartCard({ title, buckets }: CardProps) {
  const rows = useMemo(
    () =>
      Object.entries(buckets)
        .map(([key, b]) => ({ key, tokens: b.inputTokens + b.outputTokens, cost: b.costUSD }))
        .sort((a, b) => b.cost - a.cost),
    [buckets]
  );

  return (
    <div className="card">
      <h2>{title}</h2>
      {rows.length === 0 ? (
        <p className="muted">No data in the selected window.</p>
      ) : (
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 10, right: 60, left: 60, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="key" />
              <YAxis yAxisId="left">
                <Label value="Token count" angle={-90} position="insideLeft" offset={-45} style={{ textAnchor: "middle" }} />
              </YAxis>
              <YAxis yAxisId="right" orientation="right">
                <Label value="Cost (USD)" angle={90} position="insideRight" offset={-45} style={{ textAnchor: "middle" }} />
              </YAxis>
              <Tooltip formatter={(value, name) => name === "Cost (USD)" ? [(value as number).toFixed(2), name] : [value, name]} />
              <Legend />
              <Bar yAxisId="left" dataKey="tokens" fill="#003a70" name="Tokens" />
              <Bar yAxisId="right" dataKey="cost" fill="#e87722" name="Cost (USD)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
